import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { 
  Home, 
  User, 
  Leaf, 
  Filter, 
  BadgeDollarSign, 
  ArrowUpDown, 
  ChevronDown, 
  ChevronUp, 
  MapPin, 
  Building,
  BarChart3, 
  TrendingUp, 
  Wallet, 
  CreditCard, 
  Clock, 
  FileText,
  TreePine,
  CloudRain,
  Wind,
  TractorIcon
} from 'lucide-react';

// Interfaces
interface CreditoToken {
  id: string;
  projeto: string;
  quantidade: number;
  dataAquisicao: string;
  valorUnitario: number;
  categoria: 'energia' | 'florestal' | 'costeiro' | 'agropecuario';
}

interface ProjetoFiltros {
  categoria: string;
  regiao: string;
}

const AreaProprietario: React.FC = () => {
  const navigate = useNavigate();
  
  // Estados
  const [filtros, setFiltros] = useState<ProjetoFiltros>({
    categoria: '',
    regiao: ''
  });
  
  const [ordenacao, setOrdenacao] = useState<string>('dataRecente');
  const [mostrarFiltros, setMostrarFiltros] = useState<boolean>(false);
  
  // Dados simulados para créditos do proprietário
  const meusCreditos: CreditoToken[] = [
    {
      id: 'TKN-2023-001',
      projeto: 'Reflorestamento Vale do Ribeira',
      quantidade: 5000,
      dataAquisicao: '2023-10-15',
      valorUnitario: 45.80,
      categoria: 'florestal'
    },
    {
      id: 'TKN-2023-002',
      projeto: 'Energia Solar Sertão Nordestino',
      quantidade: 3200,
      dataAquisicao: '2023-11-22',
      valorUnitario: 52.30,
      categoria: 'energia'
    },
    {
      id: 'TKN-2023-003',
      projeto: 'Preservação Manguezais Costeiros',
      quantidade: 2800,
      dataAquisicao: '2023-12-05',
      valorUnitario: 48.90,
      categoria: 'costeiro'
    },
    {
      id: 'TKN-2024-001',
      projeto: 'Biogás da Agricultura Familiar',
      quantidade: 1500,
      dataAquisicao: '2024-01-18',
      valorUnitario: 42.50,
      categoria: 'agropecuario'
    }
  ];
  
  // Cálculos de estatísticas
  const totalCreditos = meusCreditos.reduce((acc, credito) => acc + credito.quantidade, 0);
  const valorTotalInvestido = meusCreditos.reduce((acc, credito) => acc + (credito.quantidade * credito.valorUnitario), 0);
  const valorMedioToken = valorTotalInvestido / totalCreditos;
  
  // Dados para gráfico de distribuição (simulado)
  const distribuicaoCategoria = {
    florestal: (meusCreditos.filter(c => c.categoria === 'florestal').reduce((acc, c) => acc + c.quantidade, 0) / totalCreditos) * 100,
    energia: (meusCreditos.filter(c => c.categoria === 'energia').reduce((acc, c) => acc + c.quantidade, 0) / totalCreditos) * 100,
    costeiro: (meusCreditos.filter(c => c.categoria === 'costeiro').reduce((acc, c) => acc + c.quantidade, 0) / totalCreditos) * 100,
    agropecuario: (meusCreditos.filter(c => c.categoria === 'agropecuario').reduce((acc, c) => acc + c.quantidade, 0) / totalCreditos) * 100
  };
  
  // Regiões disponíveis para filtro
  const regioes = ['Norte', 'Nordeste', 'Centro-Oeste', 'Sudeste', 'Sul'];
  
  // Categorias disponíveis para filtro
  const categorias = [
    { id: 'energia', nome: 'Energia Renovável' },
    { id: 'florestal', nome: 'Restauração Florestal' },
    { id: 'costeiro', nome: 'Ecossistema Costeiro' },
    { id: 'agropecuario', nome: 'Atividade Agropecuária' }
  ];
  
  // Função para formatar valores monetários
  const formatarValor = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };
  
  // Função para formatar datas
  const formatarData = (dataString: string): string => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Função para obter o ícone com base na categoria
  const obterIconeCategoria = (categoria: string) => {
    switch(categoria) {
      case 'energia':
        return <Wind className="h-5 w-5 text-yellow-500" />;
      case 'florestal':
        return <TreePine className="h-5 w-5 text-green-600" />;
      case 'costeiro':
        return <CloudRain className="h-5 w-5 text-blue-500" />;
      case 'agropecuario':
        return <TractorIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <Leaf className="h-5 w-5 text-[#0e7cb4]" />;
    }
  };
  
  // Aplicar filtros aos créditos
  const creditosFiltrados = meusCreditos.filter(credito => {
    // Filtrar por categoria
    if (filtros.categoria && credito.categoria !== filtros.categoria) {
      return false;
    }
    
    // Aqui poderia ter mais lógica para filtragem por região se tivéssemos essa informação no objeto
    
    return true;
  });
  
  // Ordenar créditos
  const creditosOrdenados = [...creditosFiltrados].sort((a, b) => {
    switch(ordenacao) {
      case 'valorMaior':
        return b.valorUnitario - a.valorUnitario;
      case 'valorMenor':
        return a.valorUnitario - b.valorUnitario;
      case 'quantidadeMaior':
        return b.quantidade - a.quantidade;
      case 'quantidadeMenor':
        return a.quantidade - b.quantidade;
      case 'dataRecente':
      default:
        return new Date(b.dataAquisicao).getTime() - new Date(a.dataAquisicao).getTime();
    }
  });
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <UserHeader />
      
      {/* Título e botões de ação */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Área do Proprietário</h1>
            <p className="text-gray-600">Gerencie seus créditos de carbono e acompanhe seus projetos</p>
          </div>
          <div className="flex space-x-2">
            <Button
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
              onClick={() => navigate('/pregao-dashboard')}
            >
              <Home className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </div>
      
      {/* Conteúdo principal */}
      <div className="flex-grow p-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Coluna principal (2/3) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Seção de filtros */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <Leaf className="h-5 w-5 text-[#0e7cb4] mr-2" />
                      Meus Créditos
                    </h2>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setMostrarFiltros(!mostrarFiltros)}
                        className="border-[#0e7cb4] text-[#0e7cb4]"
                      >
                        <Filter className="h-4 w-4 mr-2" />
                        Filtros
                        {mostrarFiltros ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
                      </Button>
                      <Select
                        value={ordenacao}
                        onValueChange={(value) => setOrdenacao(value)}
                      >
                        <SelectTrigger className="w-[180px] border-[#0e7cb4] text-[#0e7cb4]">
                          <ArrowUpDown className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Ordenar por" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dataRecente">Data (Mais recente)</SelectItem>
                          <SelectItem value="valorMaior">Valor (Maior primeiro)</SelectItem>
                          <SelectItem value="valorMenor">Valor (Menor primeiro)</SelectItem>
                          <SelectItem value="quantidadeMaior">Quantidade (Maior primeiro)</SelectItem>
                          <SelectItem value="quantidadeMenor">Quantidade (Menor primeiro)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Área de filtros expansível */}
                  {mostrarFiltros && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h3 className="font-medium mb-3 flex items-center">
                        <Filter className="h-4 w-4 text-[#0e7cb4] mr-2" />
                        Filtrar por:
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                          <Select
                            value={filtros.categoria}
                            onValueChange={(value) => setFiltros({...filtros, categoria: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Todas as categorias" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">Todas as categorias</SelectItem>
                              {categorias.map(categoria => (
                                <SelectItem key={categoria.id} value={categoria.id}>
                                  {categoria.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Região</label>
                          <Select
                            value={filtros.regiao}
                            onValueChange={(value) => setFiltros({...filtros, regiao: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Todas as regiões" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">Todas as regiões</SelectItem>
                              {regioes.map(regiao => (
                                <SelectItem key={regiao} value={regiao.toLowerCase()}>
                                  {regiao}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Lista de créditos */}
                  <div className="space-y-4">
                    {creditosOrdenados.length > 0 ? (
                      creditosOrdenados.map(credito => (
                        <Card key={credito.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start">
                                <div className="rounded-full p-3 bg-[#0e7cb4]/10 mr-3">
                                  {obterIconeCategoria(credito.categoria)}
                                </div>
                                <div>
                                  <h3 className="font-semibold text-gray-800">{credito.projeto}</h3>
                                  <p className="text-sm text-gray-500">ID: {credito.id}</p>
                                  <div className="flex items-center mt-1">
                                    <Clock className="h-4 w-4 text-gray-400 mr-1" />
                                    <span className="text-sm text-gray-600">Adquirido em: {formatarData(credito.dataAquisicao)}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-[#0e7cb4]">
                                  {credito.quantidade.toLocaleString()} tCO₂e
                                </p>
                                <p className="text-sm text-gray-600">
                                  Valor unitário: {formatarValor(credito.valorUnitario)}
                                </p>
                                <p className="text-sm font-medium text-gray-800">
                                  Total: {formatarValor(credito.quantidade * credito.valorUnitario)}
                                </p>
                              </div>
                            </div>
                            
                            <div className="mt-4 flex justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-[#0e7cb4] text-[#0e7cb4] hover:bg-[#0e7cb4] hover:text-white"
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Ver detalhes
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                        <Leaf className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-gray-700">Nenhum crédito encontrado</h3>
                        <p className="text-gray-500 mt-1">Ajuste os filtros ou adquira créditos de carbono</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Paginação (simulada) */}
                  {creditosOrdenados.length > 0 && (
                    <div className="mt-6 flex justify-center">
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" disabled>Anterior</Button>
                        <Button variant="outline" size="sm" className="bg-[#0e7cb4] text-white">1</Button>
                        <Button variant="outline" size="sm">2</Button>
                        <Button variant="outline" size="sm">3</Button>
                        <Button variant="outline" size="sm">Próximo</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Histórico de transações */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <Clock className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    Histórico de Transações
                  </h2>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Data
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Tipo
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Quantidade
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Valor Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {meusCreditos.map((credito) => (
                          <tr key={credito.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {formatarData(credito.dataAquisicao)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Aquisição
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {credito.quantidade.toLocaleString()} tCO₂e
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {formatarValor(credito.quantidade * credito.valorUnitario)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Coluna lateral (1/3) */}
            <div className="space-y-6">
              {/* Resumo dos créditos */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <Wallet className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    Resumo de Créditos
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-[#0e7cb4]/10 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-medium text-gray-600">Total de Créditos</h3>
                        <BadgeDollarSign className="h-5 w-5 text-[#0e7cb4]" />
                      </div>
                      <p className="text-2xl font-bold text-[#0e7cb4]">
                        {totalCreditos.toLocaleString()} tCO₂e
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-600 mb-1">Valor Total</h3>
                        <p className="text-lg font-semibold text-gray-800">
                          {formatarValor(valorTotalInvestido)}
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-600 mb-1">Valor Médio</h3>
                        <p className="text-lg font-semibold text-gray-800">
                          {formatarValor(valorMedioToken)}/tCO₂e
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Valor do token */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <TrendingUp className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    Valor do Token
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Valor Atual</h3>
                        <p className="text-2xl font-bold text-green-600">{formatarValor(52.75)}</p>
                      </div>
                      <div className="flex items-center text-green-600">
                        <TrendingUp className="h-5 w-5 mr-1" />
                        <span className="font-medium">+2.8%</span>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-600 mb-2">Histórico de Preços</h3>
                      <div className="h-24 w-full bg-white rounded border">
                        {/* Aqui poderia ser implementado um gráfico real */}
                        <div className="h-full w-full flex items-end">
                          <div className="h-40% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-30% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-60% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-50% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-75% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-65% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-80% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-90% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-85% w-[10%] bg-[#0e7cb4]"></div>
                          <div className="h-100% w-[10%] bg-[#0e7cb4]"></div>
                        </div>
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>Jan</span>
                        <span>Fev</span>
                        <span>Mar</span>
                        <span>Abr</span>
                        <span>Mai</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Distribuição dos créditos */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <BarChart3 className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    Distribuição dos Créditos
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center">
                            <TreePine className="h-4 w-4 text-green-600 mr-1" />
                            <span className="text-sm">Restauração Florestal</span>
                          </div>
                          <span className="text-sm font-medium">{distribuicaoCategoria.florestal.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-600 rounded-full" 
                            style={{ width: `${distribuicaoCategoria.florestal}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center">
                            <Wind className="h-4 w-4 text-yellow-500 mr-1" />
                            <span className="text-sm">Energia Renovável</span>
                          </div>
                          <span className="text-sm font-medium">{distribuicaoCategoria.energia.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-yellow-500 rounded-full" 
                            style={{ width: `${distribuicaoCategoria.energia}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center">
                            <CloudRain className="h-4 w-4 text-blue-500 mr-1" />
                            <span className="text-sm">Ecossistema Costeiro</span>
                          </div>
                          <span className="text-sm font-medium">{distribuicaoCategoria.costeiro.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${distribuicaoCategoria.costeiro}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center">
                            <TractorIcon className="h-4 w-4 text-orange-500 mr-1" />
                            <span className="text-sm">Atividade Agropecuária</span>
                          </div>
                          <span className="text-sm font-medium">{distribuicaoCategoria.agropecuario.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-orange-500 rounded-full" 
                            style={{ width: `${distribuicaoCategoria.agropecuario}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Próximos pregões */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <CreditCard className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    Próximos Pregões
                  </h2>
                  
                  <div className="space-y-3">
                    <div className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <p className="text-sm font-medium">Créditos de Mangue - Litoral Sul</p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500">15/06/2024 - 10:00</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">10.000 tCO₂e</span>
                      </div>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <p className="text-sm font-medium">Energia Eólica - Nordeste</p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500">22/06/2024 - 14:00</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">15.000 tCO₂e</span>
                      </div>
                    </div>
                    
                    <div className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <p className="text-sm font-medium">Reflorestamento Amazônia</p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500">30/06/2024 - 09:00</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">20.000 tCO₂e</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-center mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[#0e7cb4] border-[#0e7cb4]"
                      >
                        Ver todos os pregões
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      {/* Rodapé */}
      <Footer />
    </div>
  );
};

export default AreaProprietario; 